import { useMemo, useState } from "react";
import { CrmLead, STAGES, CLASSIFICATIONS, brl, stageLabel } from "@/lib/crm";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { GripVertical, Search } from "lucide-react";

interface Props {
  leads: CrmLead[];
  onOpenLead: (lead: CrmLead) => void;
  onMoveStage: (lead: CrmLead, stage: string) => void;
}

const CrmPipeline = ({ leads, onOpenLead, onMoveStage }: Props) => {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return leads;
    return leads.filter((l) =>
      [
        l.client_name, l.client_phone, l.client_whatsapp, l.client_email, l.cpf, l.neighborhood,
        l.source, l.campaign, l.classification, l.price_range, l.bedrooms, l.broker,
        l.property_name, l.city, l.general_notes, ...(l.regions || []), ...(l.interests || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [leads, q]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome, telefone, e-mail, CPF, bairro, campanha…"
          className="pl-9 font-body"
        />
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4">
        {STAGES.map((stage) => {
          const list = filtered.filter((l) => l.stage === stage.value);
          const total = list.reduce((t, l) => t + (l.deal_value || 0), 0);
          return (
            <div
              key={stage.value}
              onDragOver={(e) => {
                e.preventDefault();
                setOverStage(stage.value);
              }}
              onDragLeave={() => setOverStage((s) => (s === stage.value ? null : s))}
              onDrop={() => {
                const lead = leads.find((l) => l.id === dragId);
                if (lead && lead.stage !== stage.value) onMoveStage(lead, stage.value);
                setDragId(null);
                setOverStage(null);
              }}
              className={`w-64 shrink-0 rounded-lg border p-2 transition-colors ${
                overStage === stage.value ? "border-accent bg-accent/10" : "border-border bg-muted/40"
              }`}
            >
              <div className="px-1 pb-2 mb-2 border-b border-border">
                <p className="font-display text-xs font-bold text-foreground uppercase tracking-wide">
                  {stage.label}
                </p>
                <p className="text-[11px] font-body text-muted-foreground">
                  {list.length} lead{list.length === 1 ? "" : "s"}
                  {total > 0 && ` · ${brl(total)}`}
                </p>
              </div>

              <div className="space-y-2 min-h-[60px]">
                {list.map((lead) => {
                  const cls = CLASSIFICATIONS[lead.classification] || CLASSIFICATIONS.morno;
                  return (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={() => setDragId(lead.id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={() => onOpenLead(lead)}
                      className={`group cursor-pointer rounded-md border border-border bg-card p-2.5 shadow-sm hover:border-accent hover:shadow-md transition-all ${
                        dragId === lead.id ? "opacity-50" : ""
                      }`}
                    >
                      <div className="flex items-start gap-1">
                        <GripVertical className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-body text-sm font-semibold text-foreground truncate">
                            {lead.client_name}
                          </p>
                          <p className="text-[11px] text-muted-foreground font-body truncate">
                            {lead.neighborhood || lead.property_name || stageLabel(lead.stage)}
                          </p>
                          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 font-body ${cls.className}`}>
                              {cls.label}
                            </Badge>
                            <span className="text-[10px] font-body text-muted-foreground">
                              {lead.score} pts
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CrmPipeline;
